import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Button } from 'antd';
import useUploadDocument from '../../hooks/useUploadDocument';
import UploadDocumentDragger from './UploadDocumentDragger';
import { confirmationModal, errorModal, infoModal, successModal, ConfirmationModal } from '../Modals';
import { Alert, Select } from 'ALIASED-antd';
import { getFileExtension } from 'vl-common/src/utils';

// TODO: CSS selectors that use Antd's internal class names will break when we upgrade Antd.
const StyledModal = styled(ConfirmationModal)`
  .ant-upload.ant-upload-drag {
    width: 400px !important;
    height: 200px !important;
    @media (max-width: 600px) {
      width: 300px !important;
    }
  }

  .ant-upload-list {
    margin-top: 15px;
    margin-bottom: 15px;
    overflow-y: auto !important;
    height: 100px !important;
    background-color: #fafafa;
    box-shadow: 0px 3px 6px #00000029 !important;
  }
`;

const Dragbox = styled.div`
  width: 400px;
  margin: auto;
  @media (max-width: 600px) {
    width: 300px;
  }
`;

const UploadInfo = styled.div`
  font-size: 10px;
  width: 400px !important;
  margin: 0 auto;
`;

const UploadTypeLabel = styled.span`
  display: inline-block;
  margin-bottom: 4px;

  .asterix {
    color: var(--ant-primary-5);
  }
`;

interface UploadModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  afterDocumentUpload: () => void;
  caseCondition?: string;
  isPatientDocsTabUpload?: boolean;
}

interface UploadByAppIdModalProps extends UploadModalProps {
  appId: string;
  caseId?: string;
  patientId: string;
}
interface UploadByCaseIdModalProps extends UploadModalProps {
  appId?: string;
  caseId: string;
  patientId: string;
}

const UploadDocumentModal = ({
  visible,
  setVisible,
  appId,
  caseId,
  caseCondition,
  patientId,
  afterDocumentUpload,
  isPatientDocsTabUpload
}: UploadByAppIdModalProps | UploadByCaseIdModalProps) => {
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);

  const { files, setFiles, uploadDocs, fileTypes, setSelectedFileType, selectedFileType, modal } = useUploadDocument({
    userId: patientId,
    appId,
    caseId,
    isPatientDocsTabUpload,
    afterFileUpload: () => afterDocumentUpload()
  });

  const hasWordDoc = files.some((file) => ['docx', 'doc'].includes(getFileExtension(file.name)));

  useEffect(() => {
    if (!modal) return;
    const { type, ...rest } = modal;
    if (!type) return;

    switch (true) {
      case type === 'success':
        successModal(rest);
        break;
      case type === 'error':
        errorModal(rest);
        break;
      case type === 'confirm':
        confirmationModal(rest);
        break;
      default:
        infoModal(rest);
    }
  }, [modal]);

  const closeModal = () => {
    setSelectedFileType(null);
    setVisible(false);
    setFiles([]);
  };

  const onOk = async () => {
    setLoading(true);
    try {
      await uploadDocs(files);
    } catch (e) {
      console.log('Error while uploading file ', e);
    } finally {
      setLoading(false);
      closeModal();
    }
  };
  const onCancel = () => closeModal();

  return (
    <StyledModal
      title="Upload document"
      open={visible}
      onCancel={onCancel}
      okText={'Upload'}
      onOk={onOk}
      data-testid="upload-documents-modal"
      okButtonProps={{
        disabled: loading || files.length === 0 || files.some((f) => f.status === 'error'),
        loading
      }}
      footer={[
        <Button key="cancel" data-testid="cancel-upload-button" onClick={onCancel}>
          {t('Cancel')}
        </Button>,
        <Button
          key="upload"
          type="primary"
          data-testid="confirm-upload-button"
          onClick={onOk}
          disabled={loading || files.length === 0 || files.some((f) => f.status === 'error')}
          loading={loading}
        >
          {t('Upload')}
        </Button>
      ]}
    >
      <p style={{ marginBottom: '16px', textAlign: 'center' }}>
        {caseCondition ? (
          <span>
            &nbsp; Uploading document to: <b>{caseCondition}</b>. <br />{' '}
            {t('If you wish to upload a document to a different condition then switch to the oth')}
            <br />
            <br />
          </span>
        ) : (
          ''
        )}
        {t('Browse your computer and select a document or image to upload.')}
      </p>

      <UploadTypeLabel>
        {t('Select document type')}
        <span className="asterix">&#42;</span>
      </UploadTypeLabel>
      <Select
        options={fileTypes}
        value={selectedFileType}
        onChange={setSelectedFileType}
        style={{ width: '100%', marginBottom: '16px' }}
        placeholder={t('Document type')}
        data-testid="upload-document-type-select"
      />

      <Dragbox>
        <UploadDocumentDragger
          isFile={files.length > 0}
          files={files}
          setFiles={setFiles}
          disabled={!selectedFileType}
        />
      </Dragbox>
      {hasWordDoc && (
        <Alert
          type="warning"
          showIcon
          style={{ width: '100%', marginBottom: '16px' }}
          description={t('Clinicians cannot preview .doc or .docx files. Please upload your document as a')}
        />
      )}
      <UploadInfo>
        {t('Files must be less than 10MB')}
        <br />
        {t('Common file types allowed e.g.')} <strong>{t('PDF, DOC, DOCX, PNG, TXT, etc.')}</strong>
      </UploadInfo>
    </StyledModal>
  );
};

export default UploadDocumentModal;
